/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2015, 2018
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import * as React from 'react';
import { NavigatorWrapper,
	GlassContextHelper,
	ContentSort,
	UIHelper,
	IconHelper,
	ContentStoreObject,
	ContentServiceUrls,
	CA } from '@businessanalytics/content-nav';
import {
	classnames as cx
} from 'ca-ui-carbon-toolkit';
import PropTypes from 'prop-types';
import ListUtils from '../../utils/ListUtils';
import { AddFolderAction, PackageAction, UploadFileAction } from '../../actions';
import moment from 'moment';
import styles from './ContentList.scss';

const contentListClassName = styles.contentListClassName.replace(/"/g, '');
const classNames = cx(contentListClassName);

class ContentList extends React.Component {
	static propTypes = {
		glassContext: PropTypes.object,
		rootSlideout: PropTypes.object,
		DateTimeUtils: PropTypes.object,
		l10n: PropTypes.object,
		i18nFormatter: PropTypes.object,
		dataURL: PropTypes.string,
		contentView: PropTypes.object,
		allowUploadFiles: PropTypes.bool,
		slideoutOpen: PropTypes.bool,
		contentType: PropTypes.string,
		containerURL: PropTypes.string,
		folderSearchController: PropTypes.object,
		filterViewController: PropTypes.object,
		contentListSortIndexes: PropTypes.object,
		contentListFilterItems: PropTypes.array,
		contentListFacets: PropTypes.object,
		shortcutTargetFolder: PropTypes.object,
		id: PropTypes.string,
		showTenantInfo: PropTypes.boolean
	};

	constructor(props) {
		super(props);
		let containerURL;

		if (props.containerURL) {
			containerURL = props.containerURL;
		} else if (props.contentView.url) {
			containerURL = props.contentView.url;
		} else {
			containerURL = null;
		}

		let currentFilterTypes = {};
		for (let i = 0; i < props.contentListFilterItems.length; i++) {
			currentFilterTypes[props.contentListFilterItems[i]] = false;
		}

		this.state = {
			loaded: false,
			files: [],
			filteredFiles: [],
			unFormattedFiles: [],
			ancestors: props.contentView.ancestors || [],
			sortBy: 'name',
			sortOrder: 'asc',
			showNewFileInput: false,
			fileAddInProgress: false,
			currentFilterTypes: currentFilterTypes,
			currentFilterTypeFacets: [],
			currentFilterKeyWord: '',
			currentFilterModified: [''],
			filterDirty: false,
			updatedPropertiesObject: {},
			containerUrl: containerURL,
			sortViewOpen: false,
			urlViewOpen: false,
			filterViewOpen: false,
			isDeleted: false,
			grouped: false
		};
		const listUtilsOptions = {
			glassContext: props.glassContext
		};
		this.listUtils = new ListUtils(listUtilsOptions);
	}

	componentDidMount() {
		const url = this.state.containerUrl || null;
		const p1 = this.fetchListData(url);
		const p2 = this.fetchRootAncestor();
		return Promise.all([p1, p2])
			.then(() => {
				return Promise.resolve();
			})
			.catch((err) => {
				return Promise.reject(err);
			});
	}

	componentDidUpdate(prevProps) {
		if ((this.props.slideoutOpen !== null) && (prevProps.slideoutOpen !== this.props.slideoutOpen)) {
			if (this.props.shortcutTargetFolder) {
				let url;
				if (UIHelper.isContainer(this.props.shortcutTargetFolder.type) && this.props.shortcutTargetFolder._meta) {
					url = this.props.shortcutTargetFolder._meta.links.items.url;
					this.setState({
						containerUrl: url
					}, () => {
						this.navigateAction(this.props.shortcutTargetFolder, this.props.shortcutTargetFolder.ancestors);
						this.closeURLView();
					});
				} else if (this.props.shortcutTargetFolder.ancestors && this.props.shortcutTargetFolder.ancestors.length > 0) {
					const ancestor = this.props.shortcutTargetFolder.ancestors[this.props.shortcutTargetFolder.ancestors.length-1];
					url = ancestor._meta.links.items.url;
					this.props.shortcutTargetFolder.ancestors.pop();
					this.setState({
						containerUrl: url
					}, () => {
						this.navigateAction(ancestor, this.props.shortcutTargetFolder.ancestors);
						this.closeURLView();
					});
				}
			} else {
				this.fetchListData(this.state.containerUrl);
				this.closeURLView();
			}
		}
	}

	fetchRootAncestor = () => {
		const { l10n } = this.props;
		if (this.state.ancestors.length === 0){
			const { dataURL } = this.props;
			const options = this.listUtils.getOptions(dataURL);
			return this.listUtils.fetchData(options)
				.then((data) => {
					const ancestor = data.data.data;
					if (ancestor[0].defaultName === 'My Folders') {
						ancestor[0].defaultName = l10n.get('myContent');
					} else if (ancestor[0].defaultName === 'Team Content') {
						ancestor[0].defaultName = l10n.get('teamContent');
					}
					this.setState({
						ancestors: data.data.data,
						showNewFileInput: false
					});
					return Promise.resolve(data.data.data);
				})
				.catch(err => {
					return Promise.reject(err);
				});
		}
	}

	fetchListData = (url) => {
		const { DateTimeUtils, dataURL, showTenantInfo } = this.props;
		const requestURL = url ? url : this.listUtils.getItemsURL(dataURL);
		const options = this.listUtils.getOptions(requestURL);
		if (showTenantInfo) {
			options.data.fields = options.data.fields.concat(',tenantID');
		}
		const { glassContext } = this.props;
		return this.listUtils.fetchData(options)
			.then((data) => {
				const dataWithSortIndexes = this.calculateSortIndexes(data.data.data);
				const formattedData = this.listUtils.formatListData(DateTimeUtils, glassContext, dataWithSortIndexes);
				let formattedFiles = this.sortFiles(formattedData, this.state.sortBy, this.state.sortOrder);
				let unFormattedData = this.sortFiles(dataWithSortIndexes, this.state.sortBy, this.state.sortOrder);
				this.setState({
					unFormattedFiles: unFormattedData,
					files: formattedFiles,
					loaded: true,
					showNewFileInput: false
				}, () => {
					if (this.state.filterDirty) {
						this.filterFiles(this.state.files, this.state.currentFilterKeyWord, this.state.currentFilterModified);
					}
				});
				return Promise.resolve(formattedFiles);
			})
			.catch(err => {
				return Promise.reject(err);
			});
	}

	calculateSortIndexes = (data) => {
		const { contentListSortIndexes } = this.props;
		const items = data;
		items.forEach((item) => {
			const dataType = UIHelper.getDisplayType(item);
			if (UIHelper.isContainer(dataType)) {
				item.groupByFolder = contentListSortIndexes.folders;
				item.groupByType = item.groupByFolder;
			} else {
				item.groupByFolder = contentListSortIndexes.others;
				if (UIHelper.isGroup(dataType, 'report')) {
					item.groupByType = contentListSortIndexes.reports;
				} else if (UIHelper.isGroup(dataType, 'dashboard')) {
					item.groupByType = contentListSortIndexes.dashboards;
				} else if (UIHelper.isGroup(dataType, 'data')) {
					item.groupByType = contentListSortIndexes.data;
				} else if (UIHelper.isGroup(dataType, 'exploration')) {
					item.groupByType = contentListSortIndexes.explorations;
				} else {
					item.groupByType = contentListSortIndexes.others;
				}
			}
		});
		return items;
	}

	isAncestorTeamContent = (ancestors) => {
		const isTeamContent = ancestors.filter((item) => {
			return ContentStoreObject.isTeamContent(item);
		});
		return isTeamContent.length > 0;
	}

	cleanMyContentAncestors = (ancestors) => {
		let i = 0;
		while (ancestors[i].defaultName !== 'My Folders' && i < ancestors.length) {
			ancestors.shift();
		}
		return ancestors;
	}

	getItemAtIndex = index => this.state.filterDirty ? this.state.filteredFiles[index] : this.state.unFormattedFiles[index];

	onAction = (index) => {
		const { glassContext, contentView, contentType } = this.props;
		const item = this.getItemAtIndex(index);
		const payLoad = this.listUtils.buildPayload(item, glassContext, contentView);
		const actionId = this.listUtils.getActionId(item);
		const itemContext = payLoad.target.activeObject.aSelectedContext[0];

		if (actionId === 'com.ibm.bi.contentApps.defaultAction.folder') {
			return this.navigateAction(item);
		} else if (actionId === 'com.ibm.bi.contentApps.defaultAction.shortcut' && item.target[0].type === 'folder') {
			return this.listUtils.fetchData({ url: `${ContentServiceUrls.getBaseSearchPathURL()}${item.target[0].searchPath}`, data: { fields: 'ancestors,defaultName' } })
				.then((target) => {
					if (this.isAncestorTeamContent(target.data.data[0].ancestors) && contentType === 'teamContent') {
						this.navigateAction(target.data.data[0], target.data.data[0].ancestors);
					} else if (!this.isAncestorTeamContent(target.data.data[0].ancestors) && contentType === 'myContent') {
						const ancestors = this.cleanMyContentAncestors(target.data.data[0].ancestors);
						this.navigateAction(target.data.data[0], ancestors);
					} else {
						return this.performAction(actionId, payLoad);
					}
				});
		} else if (actionId === 'com.ibm.bi.contentApps.defaultAction.package' && PackageAction.isDefaultActionView(itemContext) && PackageAction.canExecute(itemContext)){
			return this.navigateAction(item);
		} else {
			return this.performAction(actionId, payLoad);
		}
	}

	performAction = (actionId, payLoad) => {
		const { glassContext, l10n } = this.props;
		return glassContext.appController.performAction(actionId, payLoad)
			.then(function() {
				return Promise.resolve();
			}.bind(this))
			.catch(function(err) {
				glassContext.getCoreSvc('.Logger').error(err);
				GlassContextHelper.displayToast(glassContext, l10n.get('toastLaunchPerspectiveError', {
					objectType: UIHelper.getTypeNameFromObject(payLoad.target.activeObject.aSelectedContext[0], l10n)
				}), {
					type: 'error'
				});
				return Promise.reject(err);
			}.bind(this));
	}

	navigateAction = (item, ancestors) => {
		const { rootSlideout } = this.props;
		if (item._meta.links.items && !this.containsAncestor(item, this.state.ancestors))  {
			this.fetchListData(item._meta.links.items.url);
			this.setState({
				ancestors: ancestors ? [...ancestors, item] : [...this.state.ancestors, item],
				containerUrl: item._meta.links.items.url
			}, () => {
				if (rootSlideout.child && rootSlideout.child.onHide) {
					rootSlideout.child.onHide();
				} else if (rootSlideout.child && rootSlideout.child.hide) {
					rootSlideout.child.hide();
				}
			});
		}
	}

	containsAncestor (item, ancestors) {
		let ancestor = ancestors.filter( ancestorItem => ancestorItem['id'] === item['id']);
		return ancestor.length > 0;
	}

	navBreadcrumb = (index) => {
		const { rootSlideout } = this.props;
		const ancestors = this.listUtils.navBreadcrumb(index, this.state.ancestors);
		const item = ancestors[(ancestors.length - 1)];
		const url = item._meta.links.items.url;
		this.setState({
			ancestors: ancestors,
			containerUrl: url
		}, () => {
			const item = this.state.ancestors[(this.state.ancestors.length - 1)];
			const url = item._meta.links.items.url;
			this.fetchListData(url);
			if (rootSlideout.child && rootSlideout.child.onHide) {
				rootSlideout.child.onHide();
			} else if (rootSlideout.child && rootSlideout.child.hide) {
				rootSlideout.child.hide();
			}
		});
	}

	navBackBreadcrumb = () => {
		const { rootSlideout } = this.props;
		const ancestorsStack = [...this.state.ancestors];
		const ancestors = this.listUtils.navBackBreadcrumb(ancestorsStack);
		const item = ancestors[(ancestors.length - 1)];
		const url = item._meta.links.items.url;
		this.setState({
			ancestors: ancestors,
			containerUrl: url
		}, () => {
			const item = this.state.ancestors[(this.state.ancestors.length - 1)];
			const url = item._meta.links.items.url;
			this.fetchListData(url);
			if (rootSlideout.child && rootSlideout.child.onHide) {
				rootSlideout.child.onHide();
			} else if (rootSlideout.child && rootSlideout.child.hide) {
				rootSlideout.child.hide();
			}
		});
	}

	sortFiles = (filesList, sortBy, sortOrder) => {
		if (filesList.length > 0) {
			let unSortedFiles = filesList;
			let folderGroup = [];
			let otherGroup = [];
			unSortedFiles.forEach((file) => {
				if (file.groupByFolder === '0') {
					folderGroup.push(file);
				} else {
					otherGroup.push(file);
				}
			});
			if (sortBy === 'name') {
				this.setState({
					grouped: false
				});
				if (sortOrder === 'asc') {
					otherGroup.sort((a, b) => ContentSort(a.defaultName.toLowerCase(), b.defaultName.toLowerCase()));
					folderGroup.sort((a, b) => ContentSort(a.defaultName.toLowerCase(), b.defaultName.toLowerCase()));
				} else if (sortOrder === 'desc') {
					otherGroup.sort((a, b) => ContentSort(b.defaultName.toLowerCase(), a.defaultName.toLowerCase()));
					folderGroup.sort((a, b) => ContentSort(b.defaultName.toLowerCase(), a.defaultName.toLowerCase()));
				}
			} else if (sortBy === 'modificationTime') {
				this.setState({
					grouped: false
				});
				if (sortOrder === 'asc') {
					otherGroup.sort((a, b) => ContentSort(a.modificationTime.toLowerCase(), b.modificationTime.toLowerCase()));
					folderGroup.sort((a, b) => ContentSort(a.modificationTime.toLowerCase(), b.modificationTime.toLowerCase()));
				} else if (sortOrder === 'desc') {
					otherGroup.sort((a, b) => ContentSort(b.modificationTime.toLowerCase(), a.modificationTime.toLowerCase()));
					folderGroup.sort((a, b) => ContentSort(b.modificationTime.toLowerCase(), a.modificationTime.toLowerCase()));
				}
			} else if (sortBy === 'type') {
				this.setState({
					grouped: true
				});
			}
			unSortedFiles = folderGroup.concat(otherGroup);
			return unSortedFiles;
		}
		return [];
	}

	isTypeFilterDirty = (filterTypes) => {
		return filterTypes ? filterTypes.length > 0 : false;
	}

	isKeyWordFilterDirty = (keyWord) => {
		return keyWord ? keyWord.length > 0 : false;
	}

	isModificationFilterDirty = (modificationTime) => {
		return modificationTime ? modificationTime.length > 1 : false;
	}

	getFilterTypes = () => this.state.currentFilterTypes;

	getCurrentFilterTypes = () => {
		const { contentListFacets } = this.props;
		const currentFilterTypes = this.getFilterTypes();
		const currentFilterTypesKeys = Object.keys(this.getFilterTypes());
		const selectedTypes = currentFilterTypesKeys.filter((key) => {
			return currentFilterTypes[key] === true;
		});
		let filterTypes = [];
		selectedTypes.map((type) => {
			const types = contentListFacets[type];
			filterTypes = filterTypes.concat(types.split('|'));
			return filterTypes;
		});
		return filterTypes;
	}

	getColumnSpecification = () => {
		const { l10n } = this.props;
		return [{
			'type': 'Icon',
			'accessibleLabel': l10n.get('itemType')
		}, {
			'type': 'Name',
			'scope': 'row'
		}, {
			'type': 'Time',
			'propertyName': ContentStoreObject.MODIFICATION_TIME
		}, {
			'type': 'ContextMenu'
		}];
	}

	filterFiles = (files, currentFilterKeyWord, currentFilterModified) => {
		let filteredFiles = [];
		const typesToFilter = this.getCurrentFilterTypes();
		const typeFilterDirty = this.isTypeFilterDirty(typesToFilter);
		const keyWordFilterDirty = this.isKeyWordFilterDirty(currentFilterKeyWord);
		const modificationFilterDirty = this.isModificationFilterDirty(currentFilterModified);
		const allFiltersDirty = typeFilterDirty || keyWordFilterDirty || modificationFilterDirty;

		let filteredFilesType = files;
		if (typeFilterDirty) {
			filteredFilesType = files.filter((file) => typesToFilter.includes(file.groupType));
		}

		let filteredFilesKeyWord = files;
		if (keyWordFilterDirty) {
			filteredFilesKeyWord = files.filter((file) => {
				const defaultName = file.defaultName.toLowerCase();
				return defaultName.indexOf(currentFilterKeyWord.toLowerCase()) !== -1;
			});
		}

		let filteredFilesModified = files;
		if (modificationFilterDirty) {
			filteredFilesModified = files.filter((file) => {
				return moment(file.modificationTime).isBetween(currentFilterModified[0], currentFilterModified[1]);
			});
		}

		filteredFiles = filteredFilesType.filter(item => filteredFilesKeyWord.includes(item) && filteredFilesModified.includes(item));

		this.setState({
			filteredFiles: filteredFiles,
			filterDirty: allFiltersDirty
		});
		return filteredFiles;
	}

	closeURLView = () => {
		const { rootSlideout } = this.props;
		if(rootSlideout.child && rootSlideout.child.hide && this.state.urlViewOpen === true){
			rootSlideout.child.hide();
		}
	}

	openURLView = () => {
		const { glassContext, rootSlideout } = this.props;
		const { ancestors } = this.state;
		const item = ancestors[(ancestors.length - 1)];
		if (!this.state.urlViewOpen) {
			glassContext.appController.showSlideOut({
				'parent': rootSlideout,
				'label': 'Sort',
				'width': '400px',
				'content': {
					'module': 'bi/content_apps/ui/views/AddURLView',
					'view': this,
					'url': item,
					'renderFromReact': true
				}
			}).on('hide', function() {
				this.setState({
					urlViewOpen: false
				});
			}.bind(this));
			this.setState({
				urlViewOpen: true
			});
		}
	}

	onSort = (sortSpec) => {
		this.setState({
			sortBy: sortSpec.sortBy,
			sortOrder: sortSpec.order
		}, () => {
			let sortedFiles;
			let unFormattedData;
			if (this.state.filterDirty) {
				sortedFiles = this.sortFiles(this.state.filteredFiles, this.state.sortBy, this.state.sortOrder);
				unFormattedData = this.sortFiles(this.state.unFormattedFiles, this.state.sortBy, this.state.sortOrder);
				this.setState({
					filteredFiles: sortedFiles,
					unFormattedFiles: unFormattedData
				});
			} else {
				sortedFiles = this.sortFiles(this.state.files, this.state.sortBy, this.state.sortOrder);
				unFormattedData = this.sortFiles(this.state.unFormattedFiles, this.state.sortBy, this.state.sortOrder);
				this.setState({
					files: sortedFiles,
					unFormattedFiles: unFormattedData
				});
			}
		});
	}

	onFilterModificationTime = (filterObj) => {
		this.setState({
			currentFilterModified: filterObj.value.split('|')
		}, () => {
			this.filterFiles(this.state.files, this.state.currentFilterKeyWord, this.state.currentFilterModified);
		});
	}

	onFilterType = (filterObj) => {
		let currentTypes = this.state.currentFilterTypes;
		currentTypes[filterObj['filter']] = filterObj.state;
		this.setState({
			currentFilterTypes: currentTypes
		}, () => {
			this.filterFiles(this.state.files, this.state.currentFilterKeyWord, this.state.currentFilterModified);
		});
	}

	onSearchFilter = (keyWord) => {
		this.setState({
			currentFilterKeyWord: keyWord
		}, () => {
			this.filterFiles(this.state.files, this.state.currentFilterKeyWord, this.state.currentFilterModified);
		});
	}

	openSortView = () => {
		const { glassContext, rootSlideout, l10n, contentView } = this.props;
		if (!this.state.sortViewOpen) {
			glassContext.appController.showSlideOut({
				'parent': rootSlideout,
				'label': l10n.get('sort'),
				'width': '200px',
				'content': {
					'module': 'bacontentnav/common/SortView',
					'view': contentView,
					'onSort': this.onSort,
					'renderFromReact': true
				},
				'onHide': () => {
					this.setState({
						sortViewOpen: false
					});
				}
			});
			this.setState({
				sortViewOpen: true
			});
		}
	}

	openFilterView = () => {
		const { glassContext, rootSlideout, l10n, contentView, folderSearchController, filterViewController } = this.props;
		if (!this.state.filterViewOpen) {
			const filterView = glassContext.appController.showSlideOut({
				'parent': rootSlideout,
				'label': l10n.get('filterBy'),
				'width': '200px',
				'hideOnParentClick': false,
				'onHide': () => {
					if (this.state.filterView && this.state.filterView.hide) {
						this.state.filterView.hide();
					}
					this.setState({
						filterViewOpen: false,
						filterView: null
					});
				},
				'content': {
					'module': 'bacontentnav/common/FilterView',
					'view': contentView,
					'searchBarController': folderSearchController,
					'filterController': filterViewController,
					'renderFromReact': true,
					'onFilterType': this.onFilterType,
					'onSearchFilter': this.onSearchFilter,
					'onFilterModificationTime': this.onFilterModificationTime,
					'onClearFilter': this.onClearFilter,
					'onClearSearchFilter': this.onClearSearchFilter
				}
			});
			this.setState({
				filterViewOpen: true,
				filterView: filterView
			});
		} else {
			this.state.filterView.hide();
		}
	}

	getTargetFolder = () => this.state.ancestors[this.state.ancestors.length -1];

	addFolderHandler = (fileName) => {
		const { glassContext, l10n } = this.props;
		const targetFolder = this.getTargetFolder();
		const url = targetFolder._meta.links.items.url;
		const addFolderAction = new AddFolderAction();
		this.setState({
			showNewFileInput: false,
			fileAddInProgress: true
		});

		return addFolderAction.addFolder(glassContext, url, fileName, this.state.files, l10n)
			.then((newFile) => {
				const toastString = l10n.get('toastCreateNewFolder', { nameOfFolder: newFile });
				GlassContextHelper.displayToast(glassContext, toastString);
				this.setState({ fileAddInProgress: false });
				return this.fetchListData(this.state.containerUrl);
			})
			.catch(() => {
				this.setState({ fileAddInProgress: false });
				return this.fetchListData(this.state.containerUrl);
			});
	}

	openNewItemMenu = (value) => {
		const { glassContext, rootSlideout } = this.props;
		const { ancestors } = this.state;
		const parentObj = ancestors[(ancestors.length - 1)];

		if (value === 'newFolder') {
			this.onShowNewFileInput();
		} else if (value === 'uploadFile') {
			rootSlideout.hide();
			this.uploadFile(glassContext, parentObj, ancestors);
		} else if (value === 'URL') {
			this.openURLView();
		}
	}

	uploadFile = (glassContext, parentObj, ancestors) => {
		const options = {
			glassContext: glassContext
		};
		const uploadFileAction = new UploadFileAction(options);
		uploadFileAction.onFileUpload(parentObj, glassContext, ancestors);
	}

	onShowNewFileInput = () => {
		this.setState({ showNewFileInput: true });
	}

	onPropertiesClose = () => {
		this.fetchListData(this.state.containerUrl);
	}

	onCloseSaveDialog = () => {
		this.fetchListData(this.state.containerUrl);
	}

	getLengthOfSelected = (selected) => {
		const keys = this.getSelectedKeys(selected);
		return keys.length;
	}

	getSelectedKeys = selected => Object.keys(selected);

	onMenuClick = (selected, pos, multiSelect) => {
		const { glassContext, rootSlideout, contentView, contentType } = this.props;
		if (rootSlideout.child && rootSlideout.child.onHide) {
			rootSlideout.child.onHide();
		} else if (rootSlideout.child && rootSlideout.child.hide) {
			rootSlideout.child.hide();
		}

		contentView.onCopyMove = this.onCopyMove;
		contentView.onPropertiesClose = this.onPropertiesClose;
		contentView.ancestors = this.state.ancestors;

		let selectedItems;
		selectedItems = this.getFullObjectInformation(selected, multiSelect);
		selectedItems = this.appendAncestorsToItems(selectedItems, multiSelect);

		selectedItems = this.checkSetVersionsSupported(selectedItems);

		const menuId = CA.contextMenuId;
		const options = selectedItems;
		const position = pos;
		const actionPayload = {
			menuId,
			position,
			options,
			activeObject: {
				'aSelectedContext': selectedItems,
				'ancestors': this.state.ancestors,
				'parentSlideout': rootSlideout,
				'this': this,
				'onPropertiesClose': this.onPropertiesClose,
				'onCloseSaveDialog': this.onCloseSaveDialog,
				'renderFromReact': true,
				'contentType': contentType,
				'contentView': contentView,
				'onDelete': this.onDelete,
				'oListControl': { contentView: contentView }
			}
		};
		glassContext.appController.showContextMenu(actionPayload);
	}

	checkSetVersionsSupported = (selectedObjects) => {
		for (var i = 0; i < selectedObjects.length; i = i + 1) {
			if (!((typeof selectedObjects[i].versionsSupported) === 'boolean')) {
				selectedObjects[i].versionsSupported = UIHelper.isVersionsSupported(selectedObjects[i]);
			}
		}
		return selectedObjects;
	}

	appendAncestorsToItems = (items, multiSelect) => {
		const keys = this.getSelectedKeys(items);
		if (multiSelect) {
			for (let i = 0; i < keys.length; i++) {
				items[keys[i]]['ancestors'] = this.state.ancestors;
			}
		}
		return items;
	}

	onClearFilter = () => {
		const selectedFilterTypes = this.resetFilterTypes();
		this.setState({
			currentFilterTypes: selectedFilterTypes,
			currentFilterKeyWord: '',
			currentFilterModified: ['']
		}, () => {
			this.filterFiles(this.state.files, this.state.currentFilterKeyWord, this.state.currentFilterModified);
		});
	}

	onClearSearchFilter = () => {
		this.setState({
			currentFilterKeyWord: ''
		}, () => {
			this.filterFiles(this.state.files, this.state.currentFilterKeyWord, this.state.currentFilterModified);
		});
	}

	resetFilterTypes = () => {
		const selectedFilterTypes = this.state.currentFilterTypes;
		const keys = Object.keys(selectedFilterTypes);
		keys.forEach((key) => {
			selectedFilterTypes[key] = false;
		});
		return selectedFilterTypes;
	}

	onDelete = (selected) => {
		this.removeItems(selected, this.state.unFormattedFiles, this.state.files);
		this.setState({
			isDeleted: true
		});
	}

	onCopyMove = () => {
		this.fetchListData(this.state.containerUrl);
	}

	resetDelete = () => {
		this.setState({
			isDeleted: false
		});
	}

	removeItems = (selected, unFormattedFiles, files) => {
		const resultUnformattedFiles = unFormattedFiles.filter((item) => {
			for (let i= 0; i < selected.length; i++){
				if (item.id === selected[i].id) return false;
			}
			return true;
		});
		const resultFiles = files.filter((item) => {
			for (let i = 0; i < selected.length; i++){
				if (item.defaultName === selected[i].defaultName) return false;
			}
			return true;
		});
		this.setState({
			unFormattedFiles: resultUnformattedFiles,
			files: resultFiles
		}, () => {
			this.fetchListData(this.state.containerUrl);
		});
		return resultFiles;
	}

	updateObjectInfoHandler = (updatedObject) => {
		const updatedObjKey = Object.keys(updatedObject)[0];
		const newUpdatedPropertiesObj = this.state.updatedPropertiesObject;
		if (updatedObjKey in this.state.updatedPropertiesObject){
			delete newUpdatedPropertiesObj[updatedObjKey];
		}
		const mergedObject = { ...updatedObject, ...newUpdatedPropertiesObj };
		this.setState({
			updatedPropertiesObject: mergedObject
		});
	}

	getFullObjectInformation = (selected, multiSelect) => {
		const keys = this.getSelectedKeys(selected);
		const lengthOfSelected = this.getLengthOfSelected(selected);

		if (multiSelect) {
			const selectedItems = this.state.unFormattedFiles.filter((item) => {
				for (let i = 0; i < lengthOfSelected; i++) {
					if (item.defaultName === selected[keys[i]].defaultName) {
						return true;
					}
				}
			});
			return selectedItems;
		}
		const defaultName = selected.defaultName;
		const selectedItem = selected = this.state.unFormattedFiles.filter((item) => {
			return item.defaultName === defaultName;
		});
		return selectedItem;
	}

	onConfigure = () => {
		const { dataURL, contentView, glassContext, rootSlideout } = this.props;
		if (contentView._propertiesOpen) {
			contentView.propertiesSlideout.onHide();
		} else {
			const options = {
				glassContext,
				target: {
					activeObject: {
						parentSlideout: rootSlideout,
						onPropertiesClose: this.onPropertiesClose
					}
				}
			};
			contentView.showProperties({ selfUrl: dataURL }, options);
		}
	}

	onClickItem = (item) => {
		const { contentView } = this.props;
		if (contentView._propertiesOpen && this.getLengthOfSelected(item) === 1) {
			const selectedKeys = this.getSelectedKeys(item);
			let selectedItem = item[selectedKeys[0]];
			selectedItem = this.getFullObjectInformation(selectedItem);
			if (!this.state.navigating) {
				const options = {
					target: {
						activeObject: {
							onPropertiesClose: this.onPropertiesClose
						}
					}
				};
				contentView.showProperties(selectedItem[0], options);
			}
		}
	}

	dismissCreateNewFile = () => {
		this.setState({
			showNewFileInput: false
		});
	}

	newItemMenuOptions = () => {
		const { glassContext, allowUploadFiles } = this.props;
		let newItemMenuOptions = {
			newItemMenuContent: []
		};

		if (this.isAllowNewFolder()) {
			newItemMenuOptions.newItemMenuContent.push({
				label: 'Folder',
				value: 'newFolder',
				icon: IconHelper.folder16
			});
		}
		if (this.isAllowCreateURL() && !this.isInTeamRoot()) {
			newItemMenuOptions.newItemMenuContent.push({
				label: 'URL',
				value: 'URL',
				icon: IconHelper.link16
			});
		}

		if (glassContext.hasCapability('canUploadFiles') && allowUploadFiles && !this.isInTeamRoot()) {
			newItemMenuOptions.newItemMenuContent.push({
				label: 'Upload File',
				value: 'uploadFile',
				icon: IconHelper.upload16
			});
			return newItemMenuOptions;
		} else {
			return newItemMenuOptions;
		}
	}

	isAllowNewFolder = () => {
		const { glassContext } = this.props;
		const userProfileSettings = glassContext.services.userProfile && glassContext.services.userProfile.userProfileSettings;
		const allowNewFolder = !(userProfileSettings &&
			userProfileSettings.ui_excludedFeatures &&
			userProfileSettings.ui_excludedFeatures.ids &&
			userProfileSettings.ui_excludedFeatures.ids.indexOf('com.ibm.bi.contentApps.Folder') >= 0);

		return allowNewFolder;
	}

	isAllowCreateURL = () => {
		const { glassContext } = this.props;
		const userProfileSettings = glassContext.services.userProfile && glassContext.services.userProfile.userProfileSettings;
		const allowCreateURL = !(userProfileSettings &&
			userProfileSettings.ui_excludedFeatures &&
			userProfileSettings.ui_excludedFeatures.ids &&
			userProfileSettings.ui_excludedFeatures.ids.indexOf('com.ibm.bi.contentApps.URL') >= 0);

		return allowCreateURL;
	}

	isInTeamRoot = () => {
		const { contentType } = this.props;
		return this.state.ancestors && this.state.ancestors.length === 1 && contentType === 'teamContent';
	}

	isConfigureVisible = () => {
		const { contentView } = this.props;
		const inTeamRoot = this.isInTeamRoot();
		const hasWrite = ContentStoreObject.hasPermissions(this.state.ancestors[0], ['write']);
		if (contentView.showParentPropertiesButton) {
			return inTeamRoot && hasWrite;
		}
	}

	isSortVisible = () => {
		const { contentView } = this.props;
		return contentView.showSort;
	}

	isFilterVisible = () => {
		const { contentView } = this.props;
		return contentView.showFilter;
	}

	isNewItemMenuVisible = () => {
		const { contentView } = this.props;
		return contentView.showNewItem;
	}

	isShowAddFolderVisible = () => {
		const { contentView } = this.props;
		return contentView.showAddFolder;
	}

	onGroupBy = (result, zip) => {
		const { l10n, contentListSortIndexes } = this.props;

		const groups = zip(result, (name) => {
			const caGroups = CA.uiTypes[name].groups;
			if (caGroups && caGroups.length > 0) {
				name = caGroups[0];
			}
			switch (name) {
				case 'folder':
					return l10n.get('folders');
				case 'dashboard':
					return l10n.get('dashboards');
				case 'data':
					return l10n.get('data');
				case 'exploration':
					return l10n.get('explorations');
				case 'report':
					return l10n.get('reports');
				default:
					return l10n.get('others');
			}
		});
		if (this.state.sortOrder === 'asc') {
			groups.sort((a, b) => ContentSort(contentListSortIndexes[a[0].toLowerCase()], contentListSortIndexes[b[0].toLowerCase()]));
		} else if (this.state.sortOrder === 'desc') {
			groups.sort((a, b) => ContentSort(contentListSortIndexes[b[0].toLowerCase()], contentListSortIndexes[a[0].toLowerCase()]));
		}
		return groups;
	}

	onResize = () => {
		const { rootSlideout, id } = this.props;
		const width = rootSlideout.$el[0].offsetWidth;
		UIHelper.saveSlideoutWidth(id, width);
	}

	render() {
		const { l10n, contentType, contentView } = this.props;
		const emptyIcon = contentType === 'myContent' ? IconHelper.myContent128 : IconHelper.teamContent128;
		const ancestors = contentView.showBreadcrumbs ? this.state.ancestors : [];
		const noFilterResultString = l10n.get('noFilterResult');
		const tryOtherFilterString = l10n.get('tryOtherFilter');
		return (
			<div className={classNames}>
				<NavigatorWrapper
					fileAddInProgress={this.state.fileAddInProgress}
					onMenuClick={this.onMenuClick}
					loaded={this.state.loaded}
					files={this.state.filterDirty ? this.state.filteredFiles : this.state.files}
					isFilterDirty={this.state.filterDirty}
					onGroupBy={this.onGroupBy}
					grouped={this.state.grouped}
					renderToolbar={true}
					onAction={this.onAction}
					ancestors={ancestors}
					navBreadcrumb={this.navBreadcrumb}
					navBackBreadcrumb={this.navBackBreadcrumb}
					sortHandler={this.openSortView}
					filterHandler={this.openFilterView}
					addFolderHandler={this.addFolderHandler}
					height='100%'
					newItemMenuOptions={this.newItemMenuOptions()}
					showNewFileInput={this.state.showNewFileInput}
					multiSelectCancelLabel={l10n.get('cancel')}
					onMultiSelectMenu={this.onMultiSelectMenu}
					emptyStateLabel={this.state.filterDirty ? `${noFilterResultString} ${tryOtherFilterString}` : l10n.get('emptyTableContentMessage')}
					isDeleted={this.state.isDeleted}
					resetDelete={this.resetDelete}
					dismissCreateNewFile={this.dismissCreateNewFile}
					newFilePlaceholder={l10n.get('newFolderLabel')}
					onOpenNewItemMenu={this.openNewItemMenu}
					onConfigure={this.onConfigure}
					emptyIcon={emptyIcon}
					showConfigure={this.isConfigureVisible()}
					showSort={this.isSortVisible()}
					showFilter={this.isFilterVisible()}
					showNewItemMenu={this.isNewItemMenuVisible()}
					showAddFolder={this.isShowAddFolderVisible()}
					onShowNewFileInput={this.onShowNewFileInput}
					onResize={this.onResize}
					onClickItem={this.onClickItem}/>
			</div>
		);
	}
}

export default ContentList;